import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icon'
import { useSupportAddon } from '@/hooks/useSupportAddon'
import { SupportTicket, SupportTicketReply, getTicketStatusColor, getTicketPriorityColor, Toggle, AddonInactive } from './shared'

interface TeamMember {
  id: string
  name: string
  email: string
  role: string
}

export const SupportTicketDetail: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const [reply, setReply] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [replyError, setReplyError] = useState('')
  const { active: addonActive, isLoading: addonLoading } = useSupportAddon()

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['support-module-ticket', id],
    queryFn: async () => {
      const response = await api.get(`/support/tickets/${id}`)
      return response.data.data as SupportTicket
    },
    enabled: !!id && addonActive,
  })

  const { data: replies, isLoading: repliesLoading } = useQuery({
    queryKey: ['support-module-ticket-replies', id],
    queryFn: async () => {
      const response = await api.get(`/support/tickets/${id}/replies?includeInternal=true`)
      return (response.data.data || []) as SupportTicketReply[]
    },
    enabled: !!id,
  })

  const { data: teamMembers } = useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const response = await api.get('/team')
      return (response.data.data || []) as TeamMember[]
    },
  })

  const invalidateTicket = () => {
    queryClient.invalidateQueries({ queryKey: ['support-module-ticket', id] })
    queryClient.invalidateQueries({ queryKey: ['support-module-tickets'] })
  }

  const replyMutation = useMutation({
    mutationFn: async (data: { content: string; is_internal: boolean }) => {
      const response = await api.post(`/support/tickets/${id}/replies`, data)
      return response.data
    },
    onSuccess: () => {
      setReply('')
      setIsInternal(false)
      setReplyError('')
      queryClient.invalidateQueries({ queryKey: ['support-module-ticket-replies', id] })
    },
    onError: (error: any) => {
      setReplyError(error.response?.data?.error || t('supportModule.replyError'))
    },
  })

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      const response = await api.post(`/support/tickets/${id}/status`, { status })
      return response.data
    },
    onSuccess: invalidateTicket,
  })

  const assignMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.post(`/support/tickets/${id}/assign`, { userId })
      return response.data
    },
    onSuccess: invalidateTicket,
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/support/tickets/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-module-tickets'] })
      navigate('/support-module')
    },
  })

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!reply.trim()) return
    replyMutation.mutate({ content: reply.trim(), is_internal: isInternal })
  }

  const handleDelete = () => {
    if (window.confirm(t('supportModule.deleteTicketConfirm'))) {
      deleteMutation.mutate()
    }
  }

  if (addonLoading) {
    return <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
  }

  if (!addonActive) {
    return <AddonInactive />
  }

  if (isLoading) {
    return <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
  }

  if (!ticket) {
    return (
      <div className="p-8 text-center text-gray-400">
        <p className="text-lg mb-4">{t('supportModule.ticketNotFound')}</p>
        <button onClick={() => navigate('/support-module')} className="text-[#006cd4] hover:underline">
          ← {t('supportModule.backToTickets')}
        </button>
      </div>
    )
  }

  const getStatusLabel = (status: string) =>
    t(`supportModule.status${status.charAt(0).toUpperCase()}${status.slice(1)}`)

  return (
    <div>
      <button
        onClick={() => navigate('/support-module')}
        className="text-sm text-[#006cd4] hover:text-[#0052a3] mb-6 transition-colors"
      >
        ← {t('supportModule.backToTickets')}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main: description + conversation */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <span className="text-[#006cd4] font-mono text-sm">{ticket.reference}</span>
                <h1 className="text-2xl font-bold mt-1">{ticket.subject}</h1>
              </div>
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getTicketStatusColor(ticket.status)}`}>
                {getStatusLabel(ticket.status)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTicketPriorityColor(ticket.priority)}`}>
                {t(`supportModule.priority${ticket.priority.charAt(0).toUpperCase()}${ticket.priority.slice(1)}`)}
              </span>
              <span>{format(new Date(ticket.created_at), 'PPp')}</span>
              <span>
                {ticket.customer_name || ticket.customer_email || t('supportModule.anonymous')}
                {ticket.customer_name && ticket.customer_email && (
                  <span className="text-gray-600"> · {ticket.customer_email}</span>
                )}
              </span>
            </div>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Conversation */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">{t('supportModule.replies')}</h2>
            {repliesLoading ? (
              <div className="text-gray-400 text-center py-4">{t('supportModule.loading')}</div>
            ) : !replies || replies.length === 0 ? (
              <div className="text-gray-400 text-center py-4 text-sm">{t('supportModule.noReplies')}</div>
            ) : (
              <div className="space-y-3 mb-6 max-h-[28rem] overflow-y-auto">
                {replies.map((r) => {
                  const isAgent = r.author_type === 'agent'
                  return (
                    <div key={r.id} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        r.is_internal
                          ? 'bg-yellow-500/10 border border-yellow-500/30'
                          : isAgent
                            ? 'bg-[#006cd4]/20 border border-[#006cd4]/30'
                            : 'bg-[#1a1a25] border border-white/10'
                      }`}>
                        <div className="flex items-center gap-2 mb-1 text-xs">
                          <span className="font-medium">
                            {isAgent
                              ? (r.agent_name || t('support.you'))
                              : r.author_type === 'bot'
                                ? 'Bot'
                                : (r.customer_name || r.customer_email || t('supportModule.anonymous'))}
                          </span>
                          {r.is_internal && (
                            <span className="text-yellow-400 flex items-center gap-1">
                              <Icon name="lock" className="text-[10px]" />
                              {t('supportModule.internalNote')}
                            </span>
                          )}
                          <span className="text-gray-500">{format(new Date(r.created_at), 'PPp')}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{r.content}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {replyError && (
              <div className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">
                {replyError}
              </div>
            )}

            <form onSubmit={handleReplySubmit} className="space-y-3">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
                placeholder={t('supportModule.replyPlaceholder')}
                required
              />
              <div className="flex items-center justify-between gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <Toggle checked={isInternal} onChange={setIsInternal} label={t('supportModule.internalNote')} />
                  {t('supportModule.internalNote')}
                </label>
                <button
                  type="submit"
                  disabled={replyMutation.isPending}
                  className="px-6 py-2 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                >
                  {replyMutation.isPending ? t('common.sending') : t('supportModule.sendReply')}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar: actions */}
        <div className="space-y-6">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-300 mb-3">{t('supportModule.changeStatus')}</h3>
            <select
              value={ticket.status}
              onChange={(e) => statusMutation.mutate(e.target.value)}
              disabled={statusMutation.isPending}
              className="w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
            >
              <option value="open">{t('supportModule.statusOpen')}</option>
              <option value="pending">{t('supportModule.statusPending')}</option>
              <option value="resolved">{t('supportModule.statusResolved')}</option>
              <option value="closed">{t('supportModule.statusClosed')}</option>
            </select>
          </div>

          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-300 mb-3">{t('supportModule.assignTo')}</h3>
            <select
              value={ticket.assigned_to || ''}
              onChange={(e) => e.target.value && assignMutation.mutate(e.target.value)}
              disabled={assignMutation.isPending}
              className="w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
            >
              <option value="">{t('supportModule.unassigned')}</option>
              {(teamMembers || []).map((member) => (
                <option key={member.id} value={member.id}>{member.name} ({member.email})</option>
              ))}
            </select>
          </div>

          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="w-full py-2 text-sm text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50"
            >
              {t('supportModule.deleteTicket')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
